export type PurchaseItem = {
  time: string;
  toy: string;
  qty: number;
  amount: string;
};

export type StockItem = {
  name: string;
  value: string;
};

export type MachineItem = {
  id: number;
  partnerId: number;
  partnerName: string;
  machineName: string;
  location: string;
  status: string;
  revenue: string;
  stock: string;
  sales: string;
  totalPurchases: string;
  dailyPurchases: string;
  averageCheck: string;
  paymentMethod: string;
  health: string;
  refillDate: string;
  version: string;
  stockItems: StockItem[];
  purchases: PurchaseItem[];
};

export type PartnerListItem = {
  id: number;
  name: string;
};

const PRICE_POOL = [290, 490, 790, 990, 1190, 1490, 1990, 2490];

/**
 * Весовая модель, чтобы средний чек был ближе к 1400–1500.
 * Чем чаще встречается цена, тем выше шанс.
 */
const WEIGHTED_PRICES = [
  290,
  490,
  790,
  990, 990,
  1190, 1190,
  1490, 1490, 1490,
  1990, 1990,
  2490,
];

const PRODUCT_NAMES = [
  "Позиция #1",
  "Позиция #2",
  "Позиция #3",
  "Позиция #4",
  "Позиция #5",
  "Позиция #6",
  "Позиция #7",
  "Позиция #8",
  "Позиция #9",
  "Позиция #10",
  "Позиция #11",
  "Позиция #12",
  "Позиция #13",
  "Позиция #14",
  "Позиция #15",
  "Позиция #16",
];

const STOCK_NAMES = [
  "Позиция #1",
  "Позиция #2",
  "Позиция #3",
  "Позиция #4",
  "Позиция #5",
];

const LOCATIONS = [
  "ТРК Северный",
  "ТРЦ Южный",
  "ТЦ Город",
  "АЗС Трасса",
  "АЗС Магистраль",
  "Автомойка Чисто",
  "Паркинг Центр",
  "ТРЦ Центральный",
  "Сервис АвтоПлюс",
  "ТЦ Восточный",
  "ТЦ Парк",
  "Гипермаркет АвтоЛайн",
  "Паркинг Галерея",
  "Автомойка Блеск",
  "АЗС Городская",
];

const SURNAMES = [
"Нагуманов",
"Плюснина",
"Андреенкова",
"Карибуллин",
"Валиева",
"Олюнин",
"Газизов",
"Можаева",
"Петров",
"Андреева",
"Шамаев",
"Ахмеров",
"Габтрашитова",
"Вишняков",
"Анисимова",
"Кузин",
"Щеголева",
"Маркова",
"Баткаева",
"Полякова",
"Меркулов",
"Абдюшева",
"Фархутдинов",
"Максимова",
"Руденко",
"Паисова",
"Рузаева",
"Шашмурин",
"Арыштаева",
"Прокопенко",
"Филенко",
"Рублева",
"Салахов",
"Серво",
"Иванцов",
"Браун",
"Добрякова",
"Думановская",
"Вялкова",
"Хасанов",
"Осипенко",
"Сорокин",
"Лотова",
"Кокуркина",
"Памбухчиянц",
"Кирушев",
"Свешникова",
"Набиев",
"Егорова",
"Верешко",
"Федоров",
];

const INITIALS = [
  "А.А.",
  "А.В.",
  "А.И.",
  "В.А.",
  "В.Д.",
  "В.И.",
  "Д.А.",
  "Д.В.",
  "Е.И.",
  "И.А.",
  "И.О.",
  "К.А.",
  "М.В.",
  "Н.А.",
  "О.В.",
  "П.С.",
  "Р.А.",
  "С.В.",
  "Т.И.",
  "Ю.А.",
];

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function randomFromArray<T>(arr: T[]): T {
  return arr[randomInt(0, arr.length - 1)];
}

function formatMoney(value: number): string {
  return `${value.toLocaleString("ru-RU")} ₽`;
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

function randomTime(): string {
  const hour = randomInt(8, 22);
  const minute = randomInt(0, 59);
  return `${pad2(hour)}:${pad2(minute)}`;
}

function pickWeightedPrice(): number {
  return randomFromArray(WEIGHTED_PRICES);
}

function generatePartnerName(index: number): string {
  const surname = SURNAMES[index % SURNAMES.length];
  const initials = INITIALS[index % INITIALS.length];
  return `ИП ${surname} ${initials}`;
}

/**
 * Распределение аппаратов:
 * 13 партнёров × 1 = 13
 * 9 партнёров × 2 = 18
 * 4 партнёра × 3 = 12
 * 4 партнёра × 5 = 20
 * Итого 63 аппарата
 */
function getMachinesCountByPartnerIndex(index: number): number {
  if (index < 13) return 1;
  if (index < 22) return 2;
  if (index < 26) return 3;
  return 5;
}

function generatePurchases(targetRevenue: number): PurchaseItem[] {
  const result: PurchaseItem[] = [];
  let sum = 0;

  /**
   * Целимся примерно в 60–105 покупок на автомат в месяц.
   * Но главным ограничением оставляем целевую выручку.
   */
  while (sum < targetRevenue * 0.92) {
    const price = pickWeightedPrice();
    const product = randomFromArray(PRODUCT_NAMES);

    result.push({
      time: randomTime(),
      toy: product,
      qty: 1,
      amount: formatMoney(price),
    });

    sum += price;

    if (result.length > 120) break;
  }

  result.sort((a, b) => a.time.localeCompare(b.time));
  return result;
}

function calculatePurchasesSum(purchases: PurchaseItem[]): number {
  return purchases.reduce((acc, item) => {
    const num = Number(item.amount.replace(/[^\d]/g, ""));
    return acc + num;
  }, 0);
}

function generateStockItems(): StockItem[] {
  return STOCK_NAMES.map((name) => ({
    name,
    value: `${randomInt(4, 22)} шт`,
  }));
}

function generateMachineName(machineId: number): string {
  return `Батя-${machineId}`;
}

function generateMachineItem(
  machineId: number,
  partnerId: number,
  partnerName: string
): MachineItem {
  const targetRevenue = randomInt(90000, 150000);
  const purchases = generatePurchases(targetRevenue);
  const purchasesSum = calculatePurchasesSum(purchases);

  const averageCheckValue =
    purchases.length > 0 ? Math.round(purchasesSum / purchases.length) : 0;

  const monthlySalesCount = purchases.length;
  const dailyPurchases = Math.max(1, Math.round(monthlySalesCount / 30));

  const location = randomFromArray(LOCATIONS);
  const version = "1.0";

  return {
    id: machineId,
    partnerId,
    partnerName,
    machineName: generateMachineName(machineId),
    location,
    status: "Работает",
    revenue: formatMoney(targetRevenue),
    stock: `${randomInt(25, 70)} шт`,
    sales: `${monthlySalesCount} покупок`,
    totalPurchases: formatMoney(purchasesSum),
    dailyPurchases: `${dailyPurchases}`,
    averageCheck: formatMoney(averageCheckValue),
    paymentMethod: randomFromArray(["Карта", "Карта / QR", "Карта / NFC"]),
    health: randomFromArray([
      "Без ошибок",
      "Стабильная работа",
      "Плановая проверка",
    ]),
    refillDate: `${randomInt(1, 5)} дня назад`,
    version,
    stockItems: generateStockItems(),
    purchases,
  };
}

export const partnersList: PartnerListItem[] = Array.from({ length: 30 }, (_, index) => ({
  id: index + 1,
  name: generatePartnerName(index),
}));

export const partners: MachineItem[] = (() => {
  const items: MachineItem[] = [];
  let machineId = 1001;

  partnersList.forEach((partner, index) => {
    const count = getMachinesCountByPartnerIndex(index);

    for (let i = 0; i < count; i += 1) {
      items.push(generateMachineItem(machineId, partner.id, partner.name));
      machineId += 1;
    }
  });

  return items;
})();